import React, { useState } from "react";
import Modal from "react-modal";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Button } from "react-bootstrap";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import ModalHeadAndClose from "../payouts-wtf/common/ModalHeadAndClose";
import { selectToken } from "../auth/auth.selector";
import { fetchApiData } from "../../redux/reducers/commonApiSlice";
import "../../styles/ticket-system/reopen-ticket-modal.css";

const RejectTicketModal = ({
  isOpen,
  onClose,
  ticket,
  onSubmit,
  handleReload,
  userInfoData,
}) => {
  const dispatch = useDispatch();
  const token = useSelector(selectToken);

  // Form state
  const [rejectionRemarks, setRejectionRemarks] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Validation function
  const validateForm = () => {
    const errors = {};

    if (
      !rejectionRemarks ||
      rejectionRemarks.replace(/<[^>]*>/g, "").trim().length < 10
    ) {
      errors.rejectionRemarks =
        "Rejection remarks must be at least 10 characters";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error("Please fill all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        ticket_id: ticket?.uid,
        remarks: rejectionRemarks.replace(/<[^>]*>/g, "").trim(),
      };

      const result = await dispatch(
        fetchApiData({
          method: "POST",
          endpoint: "/ticketSystem/reject",
          payload: payload,
          token: token,
        })
      );

      if (result?.payload?.status) {
        toast.success(
          result.payload.message || "Ticket rejected successfully!"
        );
        resetForm();
        onClose();
        if (handleReload) {
          handleReload();
        }
        if (onSubmit) {
          onSubmit();
        }
      } else {
        toast.error(result?.payload?.message || "Failed to reject ticket");
      }
    } catch (error) {
      console.error("Error rejecting ticket:", error);
      toast.error("An error occurred while rejecting ticket");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setRejectionRemarks("");
    setFormErrors({});
  };

  // Handle modal close
  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel="Reject Ticket"
      ariaHideApp={false}
      closeTimeoutMS={300}
    >
      <div className="reopen-ticket-container">
        <div style={{ padding: "10px" }}>
          <ModalHeadAndClose
            title={`Reject Ticket - ${ticket?.ticket_no || ""}`}
            subtitle={`${ticket?.gym_name || "WTF Gym"} - ${
              ticket?.issue_type_name || ""
            } - ${ticket?.category_name || ""}`}
            onClose={handleClose}
          />
        </div>

        <div className="modal-body-content">
          {/* Rejection Remarks */}
          <div className="form-section">
            <label className="section-label required">REJECTION REMARKS</label>
            <div
              className={`reopen-textarea-wrapper ${
                formErrors.rejectionRemarks ? "error" : ""
              }`}
            >
              <ReactQuill
                value={rejectionRemarks}
                onChange={setRejectionRemarks}
                placeholder="Explain why this ticket is being rejected (e.g., duplicate ticket, opened by mistake, etc.)..."
                modules={{
                  toolbar: [
                    ["bold", "italic", "underline"],
                    [{ list: "ordered" }, { list: "bullet" }],
                    ["clean"],
                  ],
                }}
              />
              <div className="char-count">
                {rejectionRemarks.replace(/<[^>]*>/g, "").length} characters
              </div>
            </div>
            {formErrors.rejectionRemarks && (
              <span className="error-message">
                {formErrors.rejectionRemarks}
              </span>
            )}
          </div>

          {/* Action Buttons */}
          <div className="modal-actions">
            <Button
              variant="outline-secondary"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="reject-btn"
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Rejecting...
                </>
              ) : (
                "Reject Ticket"
              )}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default RejectTicketModal;
